// Fase 7A — Impostazioni receptionist: orari di lavoro, timezone, durata default.
// Accessibile alla receptionist (modifica sé stessa) e all'admin (sceglie chi).
import { useCallback, useEffect, useState } from "react";
import { Clock, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cbApiErr } from "@/lib/callbacks";
import GoogleCalendarSection from "@/pages/admin/agency/GoogleCalendarSection";
import ReceptionistChannels, {
  ReadinessCard, AdminChannelsView,
} from "@/pages/admin/agency/ReceptionistChannels";

const DOW = [["0", "Lunedì"], ["1", "Martedì"], ["2", "Mercoledì"], ["3", "Giovedì"], ["4", "Venerdì"], ["5", "Sabato"], ["6", "Domenica"]];
const TZS = ["Europe/Rome", "Europe/London", "Europe/Madrid", "Europe/Paris", "UTC"];

export default function ReceptionistSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [receptionists, setReceptionists] = useState([]);
  const [rid, setRid] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/receptionists").then((r) => {
      setReceptionists(r.data || []);
      if (r.data?.length) setRid(r.data[0].id);
    }).catch(() => setReceptionists([]));
  }, [isAdmin]);

  const load = useCallback(async () => {
    try {
      const q = isAdmin && rid ? `?receptionist_id=${rid}` : "";
      const r = await api.get(`/agency/receptionist-profile${q}`);
      setProfile(r.data);
    } catch (e) {
      if (!isAdmin) toast.error(cbApiErr(e, "Profilo non disponibile"));
      setProfile(null);
    }
  }, [isAdmin, rid]);

  useEffect(() => { if (!isAdmin || rid) load(); }, [load, isAdmin, rid]);

  const setDay = (key, patch) => {
    setProfile((p) => ({ ...p, working_hours: { ...p.working_hours, [key]: { ...(p.working_hours?.[key] || {}), ...patch } } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const q = isAdmin && rid ? `?receptionist_id=${rid}` : "";
      await api.put(`/agency/receptionist-profile${q}`, {
        timezone: profile.timezone,
        working_hours: profile.working_hours,
        default_callback_duration: Number(profile.default_callback_duration),
      });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(cbApiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="aw-receptionist-settings" className="max-w-2xl">
      <h1 className="font-display text-2xl font-black flex items-center gap-2 mb-4"><Clock className="h-6 w-6 text-[color:var(--sp-brand)]" /> Impostazioni</h1>

      {isAdmin && (
        <div className="sp-card p-4 mb-4">
          <label className="text-sm font-bold">Receptionist</label>
          <select className="sp-input mt-1" data-testid="rs-receptionist-select" value={rid || ""} onChange={(e) => setRid(e.target.value)}>
            {receptionists.map((r) => <option key={r.id} value={r.id}>{`${r.name} · ${r.email}`}</option>)}
          </select>
        </div>
      )}

      {isAdmin && <AdminChannelsView receptionistId={rid} />}
      {!isAdmin && <ReadinessCard readiness={readiness} />}

      {/* 1-2. Profilo operativo · Orari & disponibilità (sotto) */}
      {/* 3. Google Calendar */}
      <GoogleCalendarSection receptionistId={isAdmin ? rid : null} readOnly={isAdmin} />

      {/* 4-5. WhatsApp · Telefonia — canali PERSONALI (solo la receptionist) */}
      {!isAdmin && <ReceptionistChannels onReadiness={setReadiness} />}

      {!profile ? (
        <div className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">Nessun profilo da mostrare.</div>
      ) : (
        <div className="sp-card p-5 space-y-5" data-testid="rs-profile-hours">
          <div className="text-sm font-black">Profilo operativo & Orari</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold">Fuso orario</label>
              <select className="sp-input mt-1" data-testid="rs-timezone" value={profile.timezone} onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}>
                {TZS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold">Durata richiamo (min)</label>
              <input type="number" min="5" step="5" data-testid="rs-default-duration" className="sp-input mt-1"
                value={profile.default_callback_duration} onChange={(e) => setProfile({ ...profile, default_callback_duration: e.target.value })} />
            </div>
          </div>

          <div>
            <div className="text-sm font-black mb-2">Orari di lavoro</div>
            <div className="space-y-2">
              {DOW.map(([key, label]) => {
                const cfg = profile.working_hours?.[key] || {};
                const closed = !!cfg.closed;
                return (
                  <div key={key} className="flex items-center gap-2 flex-wrap" data-testid={`rs-day-${key}`}>
                    <label className="w-24 text-sm font-bold flex items-center gap-2">
                      <input type="checkbox" data-testid={`rs-day-${key}-open`} checked={!closed} onChange={(e) => setDay(key, e.target.checked ? { closed: false, start: cfg.start || "09:00", end: cfg.end || "18:00" } : { closed: true })} />
                      {label}
                    </label>
                    {closed ? (
                      <span className="text-xs text-[color:var(--sp-text-muted)] italic">Chiuso</span>
                    ) : (
                      <>
                        <input type="time" className="sp-input !w-auto !py-1" value={cfg.start || "09:00"} onChange={(e) => setDay(key, { start: e.target.value })} />
                        <span className="text-xs">→</span>
                        <input type="time" className="sp-input !w-auto !py-1" value={cfg.end || "18:00"} onChange={(e) => setDay(key, { end: e.target.value })} />
                        <span className="text-xs text-[color:var(--sp-text-muted)] ml-2">Pausa</span>
                        <input type="time" className="sp-input !w-auto !py-1" value={cfg.break_start || ""} onChange={(e) => setDay(key, { break_start: e.target.value || null })} />
                        <span className="text-xs">→</span>
                        <input type="time" className="sp-input !w-auto !py-1" value={cfg.break_end || ""} onChange={(e) => setDay(key, { break_end: e.target.value || null })} />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <button onClick={save} disabled={saving} data-testid="rs-save-btn" className="sp-btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
          </button>
        </div>
      )}
    </div>
  );
}
